/**
 * The right-hand half of the side-by-side: the passage the extraction came from.
 *
 * Three things are load-bearing here and none of them are decoration:
 *   · the snippet itself, verbatim, in monospace, so the reader compares text
 *     with text rather than a paraphrase with a number;
 *   · the page reference, always, because "somewhere in a 90-page BZO" is not
 *     a citation;
 *   · which entries this passage covers, so a reader confirming six parameters
 *     off one table row can see that it really is one table row.
 *
 * When the backend can serve the PDF, the page opens at the cited page. When it
 * cannot — the repo packs' PDFs are not served — this says so plainly instead of
 * offering a link that would 404. A dead link on a verification screen teaches
 * the reader to stop clicking, which is the one habit this gate cannot afford.
 */
import { ExternalLink, Quote } from 'lucide-react'

import { sourcePageHref } from '@/api/client'
import { Icon } from '@/components/Icon'
import { useI18n } from '@/i18n'

import type { SourceGroup } from './rulebase'

export function SourcePanel({ groups }: { groups: readonly SourceGroup[] }) {
  const { t } = useI18n()

  if (groups.length === 0) {
    return (
      <p className="text-[13px] text-muted" data-testid="source-panel-empty">
        {t('verify.noCitation')}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4" data-testid="source-panel">
      {groups.map((group, index) => {
        const citation = group.citation
        const href = sourcePageHref(group.citation)
        return (
          <figure
            key={`${citation.document_key ?? 'doc'}-${citation.page ?? 0}-${index}`}
            className="flex flex-col gap-2 rounded-sm border border-border bg-surface px-4 py-3"
            data-testid="source-citation"
            data-page={citation.page ?? ''}
          >
            <figcaption className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[12px] text-muted-2">
              <span className="font-medium text-ink-2">
                {citation.document_title ?? citation.document_key ?? t('verify.sourceTitle')}
              </span>
              <span className="num">
                {typeof citation.page === 'number'
                  ? t('verify.page', { page: citation.page })
                  : t('verify.pageUnknown')}
              </span>
            </figcaption>

            <blockquote className="flex gap-2 text-[13px] leading-relaxed text-ink">
              <Icon icon={Quote} className="mt-1 size-3.5 text-muted-2" />
              <span className="font-mono whitespace-pre-wrap">{citation.snippet}</span>
            </blockquote>

            {group.covers.filter(Boolean).length > 0 ? (
              <p className="text-[12px] text-muted">
                {t('verify.evidenceFor')}: {group.covers.filter(Boolean).join(' · ')}
              </p>
            ) : null}

            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center gap-1.5 text-[12px] text-ink-2 underline underline-offset-2"
              >
                <Icon icon={ExternalLink} className="size-3.5" />
                {t('verify.openSource')}
              </a>
            ) : (
              <p className="text-[12px] text-muted-2">{t('verify.noSourceLink')}</p>
            )}
          </figure>
        )
      })}
    </div>
  )
}

